export function formatDate(dateString: string): string {
    const date = new Date(dateString);

    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    const day = date.getDate();
    const year = date.getFullYear();
    const month = monthNames[date.getMonth()];

    // Helper to get ordinal suffix
    const getOrdinal = (n: any) => {
        if (n > 3 && n < 21) return `${n}th`;
        switch (n % 10) {
            case 1:
                return `${n}st`;
            case 2:
                return `${n}nd`;
            case 3:
                return `${n}rd`;
            default:
                return `${n}th`;
        }
    };

    return `${month} ${getOrdinal(day)} ${year}`;
}

export function formatDay(dateString: string) {
    if (!dateString) {
        return { weekday: "", rest: "" };
    }
    const date = new Date(dateString);

    const weekday = date.toLocaleDateString("en-US", {
        weekday: "short", // Wed
    });

    const rest = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return { weekday, rest };
}

export function calculateRemainingDays(dateString: string): number {
    if (!dateString) return 0;
    const scheduledDate = new Date(dateString);
    const now = new Date();
    const diffTime = scheduledDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

// A request a stakeholder submitted. Modules record the requester as
// "external", "Stakeholder"/"stakeholder", "client" (token user_type) or leave
// it empty; only staff-created requests are "internal".
export function isExternalRequest(req: any): boolean {
    return String(req?.requested_by ?? "").toLowerCase() !== "internal";
}

// Status a stakeholder request starts in, before the stakeholder forwards it
// ("traning_request_submitted" is Legal Training Provision's spelling).
const STAKEHOLDER_SUBMITTED_STATUSES = new Set(["Submitted", "traning_request_submitted"]);

// A new stakeholder request the stakeholder has not forwarded yet: still theirs
// to edit, so staff do not see it until the stakeholder presses Forward. Only
// the first submission is hidden; a request that comes back to the stakeholder
// later (e.g. for additional information) stays visible to staff.
export function isAwaitingStakeholderForward(
    req: any,
    submittedStatuses: Set<string> = STAKEHOLDER_SUBMITTED_STATUSES,
): boolean {
    if (req?.is_internal === true || !isExternalRequest(req)) return false;
    const status = req?.current_status ?? req?.status;
    if (!submittedStatuses.has(status)) return false;
    const forward = req?.forward_status;
    return !forward || forward === "not_forwarded";
}

// Rows for a list table: stakeholders see all their own requests; staff do
// not see stakeholder requests until the stakeholder has forwarded them.
// submittedStatuses: the module's first status, when it is not "Submitted"
// (legal research and committee requests start "pending" / "received").
export function visibleRequests<T>(
    rows: T[] | undefined,
    user: any,
    submittedStatuses?: Set<string>,
): T[] {
    const isStakeholder = user?.user_type === "external" || user?.user_type === "client";
    if (isStakeholder) return rows || [];
    return (rows || []).filter((row) => !isAwaitingStakeholderForward(row, submittedStatuses));
}

export function getUserType(role: string): string {
    const roleStr = role?.toLowerCase() || "";
    
    // Check for head-level roles
    if (roleStr.includes("generalattorney") || roleStr.includes("attorneygeneral") || roleStr.includes("lrdifudeputy")) {
        return "head";
    }
    
    // Check for coordinator roles (any coordinator variant, including zonal)
    // "cordinator" covers the misspelled LegalTranslationCordinator / ImpAwarenessCordinator roles
    if (roleStr.includes("coordinator") || roleStr.includes("cordinator") || roleStr.includes("zonalnif") || roleStr.includes("zonallif")) {
        return "coordinator";
    }
    
    // Check for director roles
    if (roleStr.includes("director")) {
        return "director";
    }
    
    // Check for prosecutor roles
    if (roleStr.includes("prosecutor")) {
        return "prosecutor";
    }
    
    return "head";
}

export const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }
    return (
        parts[0].charAt(0).toUpperCase() +
        parts[parts.length - 1].charAt(0).toUpperCase()
    );
};

/**
 * Validate JWT token expiration
 * @param token JWT token string
 * @param gracePeriod Buffer time in seconds before actual expiration (default: 60 seconds)
 * @returns true if token is expired or invalid
 */
export const isTokenExpired = (token: string | null, gracePeriod: number = 60): boolean => {
  if (!token || typeof token !== "string") {
    console.warn("Token is null or not a string");
    return true;
  }

  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.warn("Invalid JWT token format");
      return true;
    }

    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    
    if (!payload.exp) {
      console.warn("Token payload has no exp claim");
      return false; // If no exp claim, assume not expired
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;
    const timeUntilExpiration = expirationTime - currentTime;

    if (timeUntilExpiration <= 0) {
      console.warn(`Token has expired (expired ${Math.abs(timeUntilExpiration)} seconds ago)`);
      return true;
    }

    if (timeUntilExpiration <= gracePeriod) {
      console.warn(`Token will expire in ${timeUntilExpiration} seconds (within ${gracePeriod}s grace period)`);
      return true;
    }

    console.log(`Token is valid for ${timeUntilExpiration} seconds`);
    return false;
  } catch (error) {
    console.error("Error decoding token:", error);
    return true;
  }
};

/**
 * Get the JWT token from storage (sessionStorage or localStorage)
 * @returns token string or null if not found
 */
export const getStoredAuthToken = (): string | null => {
  try {
    const tokenData = sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
    if (!tokenData) {
      console.warn("No auth token found in sessionStorage or localStorage");
      return null;
    }

    // Try parsing token from JSON object (stored as { access: "...", access_token: "...", token: "..." })
    try {
      const parsed = JSON.parse(tokenData);
      return parsed.access || parsed.access_token || parsed.token;
    } catch {
      // Token is a raw JWT string, not JSON
      return tokenData;
    }
  } catch (e) {
    console.error("Error retrieving auth token:", e);
    return null;
  }
};

/**
 * Clear all stored authentication tokens
 */
export const clearAuthToken = (): void => {
  sessionStorage.removeItem("authToken");
  localStorage.removeItem("authToken");
  sessionStorage.removeItem("authUser");
};
